import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon } from '../../assets';
import classNames from 'classnames';

import { Pill } from '../Pill';
import { Typography } from '../Typography';
import { MultiSelectItem } from './MultiSelectItem';

import styles from './MultiSelect.module.scss';

export type TMultiSelectOption = {
    value: string;
    label: string;
};

export interface IMultiSelectProps {
    options: TMultiSelectOption[];
    selectedOptions?: TMultiSelectOption[];
    onValueChange?: (selectedOptions: TMultiSelectOption[]) => void;
    placeholder?: string;
    error?: boolean;
    disabled?: boolean;
}

export const MultiSelect = forwardRef<HTMLButtonElement, IMultiSelectProps>(
    ({ options, placeholder, error, selectedOptions: selectedOptionsControlled, onValueChange, disabled }, ref) => {
        const [selectedOptions, setSelectedOptions] = useState<TMultiSelectOption[]>(selectedOptionsControlled || []);

        const buttonRef = useRef<HTMLButtonElement>(null);
        useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

        const buttonWidth = buttonRef.current?.offsetWidth || 0;

        const handleValueSelect = (selectedValue: string) => {
            setSelectedOptions((prevSelectedOptions) => {
                const selectedOption = options.find((option) => option.value === selectedValue);

                // If no option is selected, return the previous selected options
                if (!selectedOption) {
                    onValueChange?.(prevSelectedOptions);
                    return prevSelectedOptions;
                }

                // If the option is already selected, remove it from the selected options
                if (prevSelectedOptions.some((option) => option.value === selectedValue)) {
                    const filteredOptions = prevSelectedOptions.filter((option) => option.value !== selectedValue);
                    onValueChange?.(filteredOptions);
                    return filteredOptions;
                }

                // Add the selected option to the selected options
                const newOptions = [...prevSelectedOptions, selectedOption];
                onValueChange?.(newOptions);
                return newOptions;
            });
        };

        // Update the selected options if the controlled prop changes
        useLayoutEffect(() => {
            if (selectedOptionsControlled) {
                setSelectedOptions(selectedOptionsControlled);
            }
        }, [selectedOptionsControlled]);

        return (
            <RadixSelect.Root value="" onValueChange={handleValueSelect} disabled={disabled}>
                <RadixSelect.Trigger
                    ref={buttonRef}
                    className={classNames(styles.triggerButton, {
                        [styles.error]: error,
                    })}
                >
                    <div className={styles.selectedOptions}>
                        {selectedOptions.length === 0 && placeholder && (
                            <Typography data-placeholder color="inherit">
                                {placeholder}
                            </Typography>
                        )}
                        {selectedOptions.map(({ label }) => (
                            <Pill key={label} colorMode="hard">
                                <Typography color="inherit" style={{ whiteSpace: 'nowrap' }}>
                                    {label}
                                </Typography>
                            </Pill>
                        ))}
                    </div>
                    <RadixSelect.Icon>
                        <ChevronDownIcon className={styles.triggerIcon} />
                    </RadixSelect.Icon>
                </RadixSelect.Trigger>

                <RadixSelect.Portal>
                    <RadixSelect.Content
                        sideOffset={4}
                        className={styles.content}
                        position="popper"
                        style={{ minWidth: `${buttonWidth}px` }}
                    >
                        <RadixSelect.ScrollUpButton className={styles.scrollButtonUp}>
                            <ChevronUpIcon />
                        </RadixSelect.ScrollUpButton>
                        <RadixSelect.Viewport className={styles.viewport}>
                            {options.map(({ label, value }) => (
                                <MultiSelectItem
                                    key={value}
                                    value={value}
                                    selected={selectedOptions.some((option) => option.value === value)}
                                >
                                    {label}
                                </MultiSelectItem>
                            ))}
                        </RadixSelect.Viewport>
                        <RadixSelect.ScrollDownButton className={styles.scrollButtonDown}>
                            <ChevronDownIcon />
                        </RadixSelect.ScrollDownButton>
                    </RadixSelect.Content>
                </RadixSelect.Portal>
            </RadixSelect.Root>
        );
    },
);
